import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const { FiArrowLeft, FiEdit3, FiCheckCircle, FiShield, FiZap, FiArrowRight, FiLock, FiUploadCloud, FiCpu, FiDatabase, FiBookOpen } = FiIcons;

const rewriteSamples = [
  {
    label: 'Flat opening',
    before: 'The house was old and empty. Sarah walked up to the door. She felt nervous.',
    after: 'The house had the particular stillness of a place no one had loved in years. Sarah came up the path anyway, telling herself the tightness in her chest was only the cold.'
  },
  {
    label: 'Generic advice',
    before: 'Time management is important. You should plan your day. Make a to-do list and stick to it.',
    after: 'Nobody drowns from a lack of hours - they drown from a lack of order. Decide the night before what the morning is for, and let the list, not your mood, run the day.'
  },
  {
    label: 'Stiff dialogue',
    before: '"I am very angry with you," she said. "You did not tell me the truth."',
    after: '"You let me find out on my own," she said - and somehow that was worse than any shouting could have been.'
  }
];

const dimensions = [
  { title: 'Sentence rhythm', desc: 'Average length, variation, and how you pace long and short sentences.' },
  { title: 'Vocabulary', desc: 'Your characteristic word choices and the words you tend to avoid.' },
  { title: 'Tone & register', desc: 'Formal or conversational, warm or dry, plain or lyrical.' },
  { title: 'Punctuation habits', desc: 'Em dashes, semicolons, parentheticals - the marks that signal your hand.' },
  { title: 'Imagery & metaphor', desc: 'How often you reach for figurative language, and what kind.' },
  { title: 'Dialogue & voice', desc: 'How your characters speak and how you handle attribution.' },
  { title: 'Structure', desc: 'Paragraph shape, scene transitions, and how you open and close.' }
];

const PORTRAIT = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=160&h=160&q=80`;

const styleProfiles = [
  {
    genre: 'Literary Fiction',
    author: 'Elena R.',
    image: '/authors/literary-fiction.jpg',
    sample: 'The kettle ticked as it cooled, and in the long pause she understood, finally, what he had not said.',
    dims: [['Sentence length', 72], ['Formality', 55], ['Imagery', 88], ['Pace', 32]]
  },
  {
    genre: 'Thriller',
    author: 'Marcus T.',
    image: '/authors/thriller.jpg',
    sample: 'Three minutes. The hallway was empty. He counted the doors, found the right one, and went in fast.',
    dims: [['Sentence length', 24], ['Formality', 30], ['Imagery', 34], ['Pace', 95]]
  },
  {
    genre: 'Cozy Mystery',
    author: 'Iris M.',
    image: '/authors/cozy-mystery.jpg',
    sample: 'Margaret set down her tea, quite certain now that the vicar had been lying about the marmalade.',
    dims: [['Sentence length', 56], ['Formality', 52], ['Imagery', 54], ['Pace', 48]]
  },
  {
    genre: 'Young Adult',
    author: 'Tessa K.',
    image: '/authors/young-adult.jpg',
    sample: "Okay, so technically I wasn't supposed to be on the roof. But technically is doing a lot of work there.",
    dims: [['Sentence length', 40], ['Formality', 14], ['Imagery', 44], ['Pace', 72]]
  },
  {
    genre: 'Memoir',
    author: 'David O.',
    image: '/authors/memoir.jpg',
    sample: 'I have told this story so many times that I no longer trust which parts are mine and which I borrowed.',
    dims: [['Sentence length', 64], ['Formality', 46], ['Imagery', 62], ['Pace', 38]]
  },
  {
    genre: 'Business Nonfiction',
    author: 'Ray N.',
    image: '/authors/business-nonfiction.jpg',
    sample: "Most teams don't fail from a lack of ideas. They fail from a lack of a system to choose between them.",
    dims: [['Sentence length', 44], ['Formality', 72], ['Imagery', 24], ['Pace', 60]]
  }
];

const VoiceDnaPage = () => {
  const [activeSample, setActiveSample] = useState(0);
  const [pasted, setPasted] = useState('');
  const [previewed, setPreviewed] = useState(false);
  const sample = rewriteSamples[activeSample];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Author Voice DNA - ManuscriptSmith',
    description: 'How Author Voice DNA learns your writing style across seven dimensions and writes new prose that sounds like you.',
    url: 'https://manuscriptsmith.com/voice-dna'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Author Voice DNA - Write in Your Own Voice | ManuscriptSmith"
        description="Author Voice DNA studies your writing across seven stylometric dimensions - rhythm, vocabulary, tone, punctuation - and writes new chapters that sound like you, not generic AI."
        keywords="author voice dna, write in your own voice, ai writing style, stylometry, voice dna, ai that sounds like me"
        canonicalUrl="https://manuscriptsmith.com/voice-dna"
        structuredData={structuredData}
      />

      {/* Top bar */}
      <main>
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 text-center">
          <motion.h1
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          >
            Author{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">Voice DNA</span>
          </motion.h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Generic AI flattens every author into the same smooth, anonymous voice. Author Voice DNA does the
            opposite - it learns <em>your</em> style and writes in it, so your book reads like you wrote every line.
          </p>
        </section>

        {/* Before / after */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 shadow-sm">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Generic AI</div>
              <p className="text-gray-600 leading-relaxed italic">
                "The old house stood at the end of the lane. It had been empty for many years. The windows were
                dark and the garden was overgrown. Sarah felt a chill as she approached the door."
              </p>
              <p className="mt-4 text-sm text-gray-400">Correct - and indistinguishable from a thousand other books.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border-2 border-primary-200 shadow-md">
              <div className="text-xs font-bold uppercase tracking-wide text-primary-500 mb-3">In your voice</div>
              <p className="text-gray-800 leading-relaxed italic">
                "The house waited at the end of the lane the way old things wait - patiently, and without much
                hope. Years of empty had settled into its windows. Sarah came up the path anyway, the garden
                catching at her ankles, and told herself the chill was only the weather."
              </p>
              <p className="mt-4 text-sm text-primary-600">Same scene, matched to your rhythm, imagery, and tone.</p>
            </div>
          </div>
        </section>

        {/* Seven dimensions */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Seven dimensions. 50+ measurable signals.</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                You give it a sample of your writing. It measures more than fifty stylometric signals across seven
                dimensions - then every suggestion is generated to match that profile.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {dimensions.map((d, i) => (
                <motion.div
                  key={i}
                  className="p-6 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center mb-4">
                    <SafeIcon icon={FiEdit3} className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{d.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{d.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust row */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            {[
              { icon: FiShield, t: 'Private by default', d: 'Your style profile is yours alone - never shared or used to train other models.' },
              { icon: FiZap, t: 'Consistent everywhere', d: 'Every chapter, rewrite, and outline stays in the same voice.' },
              { icon: FiCheckCircle, t: 'You stay in control', d: 'Accept, edit, or ignore any suggestion. Your words are always yours.' }
            ].map((b, i) => (
              <div key={i} className="p-6">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary-50 flex items-center justify-center mb-4">
                  <SafeIcon icon={b.icon} className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{b.t}</h3>
                <p className="text-sm text-gray-600">{b.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How your voice becomes a reusable memory */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">How your voice becomes a reusable memory</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Think of it like cloning a narrator's voice for an audiobook - but instead of how you <em>sound</em>,
                we capture how you <em>write</em>. Your style is measured, stored as a private "Voice Memory," and
                reused to write your next book in the same voice as your last.
              </p>
            </div>

            {/* 4-step process */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {[
                { icon: FiUploadCloud, t: 'Ingest', d: 'Add one to three of your existing books or chapters - or paste a sample.' },
                { icon: FiCpu, t: 'Analyze', d: 'We scan 50+ signals across 7 dimensions: rhythm, punctuation, imagery, dialogue, pacing.' },
                { icon: FiDatabase, t: 'Voice Memory', d: 'Your signals are saved as a private, reusable model of how you write.' },
                { icon: FiBookOpen, t: 'Write', d: 'Every new chapter - even a brand-new book - is generated in that same voice.' }
              ].map((s, i) => (
                <div key={s.t} className="relative bg-white rounded-2xl border border-gray-200 p-5 text-center">
                  <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center mb-3">
                    <SafeIcon icon={s.icon} className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-[11px] font-bold text-gray-400 mb-1">STEP {i + 1}</div>
                  <h3 className="font-bold text-gray-900 mb-1">{s.t}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{s.d}</p>
                  {i < 3 && <div className="hidden lg:block absolute top-1/2 -right-3 text-gray-300"><SafeIcon icon={FiArrowRight} className="w-5 h-5" /></div>}
                </div>
              ))}
            </div>

            {/* Annotated capture → Voice Memory artifact */}
            <div className="grid lg:grid-cols-2 gap-6 items-stretch">
              {/* The scan */}
              <div className="bg-gray-900 rounded-2xl p-6 shadow-xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400">Analyzing your prose</div>
                  <div className="text-[11px] text-primary-300 inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></span> scanning
                  </div>
                </div>
                <p className="text-[15px] leading-8 text-gray-100" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                  The house waited at the end of the lane{' '}
                  <span className="rounded px-1 bg-secondary-400/30 text-secondary-200">—</span>{' '}
                  <span className="rounded px-1 bg-primary-400/25">the way old things wait, patiently, and without much hope.</span>{' '}
                  <span className="rounded px-1 bg-rose-400/25">Years of empty had settled into its windows.</span>{' '}
                  Sarah came up the path anyway,{' '}
                  <span className="rounded px-1 bg-sky-400/25">the garden catching at her ankles,</span>{' '}
                  and told herself the chill was only the weather.
                </p>
                {/* legend */}
                <div className="mt-5 grid grid-cols-2 gap-2 text-[11px] text-gray-300">
                  <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-primary-400/60"></span> Long, trailing rhythm</span>
                  <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-secondary-400/60"></span> Em-dash habit</span>
                  <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-rose-400/60"></span> Sensory imagery</span>
                  <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-sky-400/60"></span> Physical detail in motion</span>
                </div>
                <div className="mt-4 text-[11px] text-gray-500">52 signals detected · 7 dimensions · this stays private to you</div>
              </div>

              {/* The memory artifact */}
              <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-6 border border-primary-100 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center">
                    <SafeIcon icon={FiDatabase} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Your Voice Memory</div>
                    <div className="text-xs text-gray-500">v1 · trained on 3 books · 84,000 words</div>
                  </div>
                  <span className="ml-auto text-[11px] text-primary-700 bg-white/70 px-2 py-1 rounded-full inline-flex items-center gap-1">
                    <SafeIcon icon={FiLock} className="w-3 h-3" /> Private
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {[
                    ['Avg sentence', '21 words'], ['Em-dashes / 1k', '7.4'],
                    ['Imagery density', 'High'], ['Register', 'Warm, literary'],
                    ['Dialogue tags', 'Minimal'], ['Paragraph shape', 'Long opens']
                  ].map(([k, v]) => (
                    <div key={k} className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                      <div className="text-[10px] uppercase tracking-wide text-gray-400">{k}</div>
                      <div className="text-sm font-semibold text-gray-800">{v}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-auto bg-white rounded-lg p-3 border border-primary-100">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-primary-500 mb-1">Reused to write your next book</div>
                  <p className="text-sm text-gray-700 italic">"He came in the way he always did — quietly, and then all at once."</p>
                  <div className="text-[11px] text-gray-400 mt-1">New chapter · 94% voice match</div>
                </div>
              </div>
            </div>

            <p className="text-center text-sm text-gray-500 mt-6 max-w-3xl mx-auto">
              Your Voice Memory is yours alone. It is never shared, never used to generate output for other writers,
              and excluded from model training. Update it any time you publish a new book, and it keeps getting more
              precisely <em>you</em>.{' '}
              <Link to="/analysis" className="text-primary-600 font-semibold hover:underline whitespace-nowrap">See a full sample report →</Link>
            </p>
          </div>
        </section>

        {/* Try a rewrite (marketing demo) */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">See a rewrite in your voice</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Pick a sample to see flat, generic text rewritten with more voice and craft - or paste your own to preview the idea.
            </p>
          </div>

          {/* sample selector */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {rewriteSamples.map((s, i) => (
              <button
                key={s.label}
                onClick={() => setActiveSample(i)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeSample === i ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-300'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Before — flat draft</div>
              <p className="text-gray-600 leading-relaxed italic">"{sample.before}"</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border-2 border-primary-200 shadow-md">
              <div className="text-xs font-bold uppercase tracking-wide text-primary-500 mb-3">After — in your voice</div>
              <p className="text-gray-800 leading-relaxed italic">"{sample.after}"</p>
            </div>
          </div>

          {/* paste-your-own (preview/marketing gate) */}
          <div className="max-w-3xl mx-auto bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Or paste a few sentences of your own</label>
            <textarea
              value={pasted}
              onChange={(e) => { setPasted(e.target.value); setPreviewed(false); }}
              rows={4}
              placeholder="Paste a paragraph from your current writing..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none text-gray-800"
            />
            <button
              onClick={() => setPreviewed(true)}
              disabled={!pasted.trim()}
              className="mt-3 inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600 disabled:opacity-50 transition-all"
            >
              <SafeIcon icon={FiEdit3} className="w-5 h-5 mr-2" /> Rewrite in my voice
            </button>
            {previewed && (
              <div className="mt-4 p-4 bg-secondary-50 border border-secondary-200 rounded-lg flex items-start">
                <SafeIcon icon={FiLock} className="w-5 h-5 text-secondary-700 mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-700">
                  <strong>Preview only.</strong> Running Author Voice DNA on your own writing is part of the full studio.{' '}
                  <Link to="/signup" className="text-primary-600 font-semibold hover:underline">Create an account</Link> to
                  build your style profile and rewrite your own text.
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Style profiles across genres */}
        <section className="bg-parchment-100 py-16 border-y border-parchment-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Every genre has its own fingerprint</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                The same scene reads differently in a thriller than in literary fiction. Voice DNA measures these
                signals - so it can match your genre <em>and</em> your personal style. Here's how a few profiles compare.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {styleProfiles.map((p, i) => (
                <motion.div
                  key={p.genre}
                  className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm flex flex-col"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img src={p.image} alt={`${p.genre} author`} loading="lazy" className="w-12 h-12 rounded-full object-cover ring-2 ring-secondary-200" />
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wide text-secondary-700">{p.genre}</div>
                      <div className="text-sm text-gray-500">{p.author}, indie author</div>
                    </div>
                  </div>
                  <p className="text-gray-700 italic leading-relaxed mb-5 flex-1">"{p.sample}"</p>
                  <div className="space-y-3">
                    {p.dims.map(([label, val]) => (
                      <div key={label}>
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{label}</span><span>{val}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className="h-2 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500"
                            style={{ width: `${val}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8">
              Illustrative profiles. Your own Voice DNA is built from your writing - not a preset.
            </p>
          </div>
        </section>

        {/* Does book two sound like book one? */}
        <section className="bg-white py-16 border-t border-parchment-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Does book two sound like book one?</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Your Voice Memory doesn't just help one book - it keeps your whole series consistent. Here's a chapter
                from each, measured against the other.
              </p>
            </div>
            <div className="grid md:grid-cols-[1fr_auto_1fr] gap-6 items-center">
              {/* Book one */}
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5">
                <div className="text-[11px] font-bold uppercase tracking-wide text-secondary-700 mb-1">Book one</div>
                <div className="font-bold text-gray-900 mb-4">Book One · Ch. 3</div>
                {[['Sentence rhythm', 74], ['Tone — warm', 80], ['Imagery', 86], ['Dialogue', 68], ['Pace', 44]].map(([l, v]) => (
                  <div key={l} className="mb-2"><div className="flex justify-between text-[11px] text-gray-500 mb-1"><span>{l}</span><span>{v}</span></div>
                    <div className="w-full bg-gray-200 rounded-full h-2"><div className="h-2 rounded-full bg-primary-500" style={{ width: `${v}%` }} /></div></div>
                ))}
              </div>
              {/* Match meter */}
              <div className="text-center">
                <div className="w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex flex-col items-center justify-center text-white shadow-lg">
                  <span className="text-3xl font-bold">96%</span><span className="text-[10px] uppercase tracking-wide opacity-90">match</span>
                </div>
                <div className="text-sm font-semibold text-emerald-700 mt-3 inline-flex items-center gap-1">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4" /> Same voice
                </div>
              </div>
              {/* Book two */}
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5">
                <div className="text-[11px] font-bold uppercase tracking-wide text-secondary-700 mb-1">Book two</div>
                <div className="font-bold text-gray-900 mb-4">Book Two · Ch. 1</div>
                {[['Sentence rhythm', 72], ['Tone — warm', 82], ['Imagery', 84], ['Dialogue', 70], ['Pace', 46]].map(([l, v]) => (
                  <div key={l} className="mb-2"><div className="flex justify-between text-[11px] text-gray-500 mb-1"><span>{l}</span><span>{v}</span></div>
                    <div className="w-full bg-gray-200 rounded-full h-2"><div className="h-2 rounded-full bg-secondary-500" style={{ width: `${v}%` }} /></div></div>
                ))}
              </div>
            </div>
            <p className="text-center text-gray-600 mt-8 max-w-2xl mx-auto">
              Same rhythm, same warmth, same eye for imagery. Your Voice Memory keeps every book in the series
              unmistakably yours - even years apart. Write the sequel and it still reads like you.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-16">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">Write your next chapter in your own voice</h2>
            <p className="text-lg opacity-90 mb-8">Get started - paste a sample and watch Author Voice DNA learn your style.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50 transition-all">
                Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/editor" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25 transition-all">
                Try the live editor demo
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default VoiceDnaPage;
